// Stamp library: canvas-rendered rubber-stamp images (Acrobat-style
// bordered uppercase text, with {date}/{name} token substitution) that
// feed the existing image-stamp placement flow, plus a tiny IndexedDB
// store for the user's own uploaded stamps.
import type { PendingStamp } from "./ui-types.ts";

export interface StampPreset {
  id: string;
  /** Menu label + stamp text (tokens substituted at render time). */
  text: string;
  color: string;
}

export const STAMP_PRESETS: readonly StampPreset[] = [
  { id: "approved", text: "APPROVED", color: "#2f9e44" },
  { id: "not-approved", text: "NOT APPROVED", color: "#e03131" },
  { id: "draft", text: "DRAFT", color: "#868e96" },
  { id: "final", text: "FINAL", color: "#1971c2" },
  { id: "confidential", text: "CONFIDENTIAL", color: "#e8590c" },
  { id: "for-review", text: "FOR REVIEW", color: "#7048e8" },
  { id: "reviewed-date", text: "REVIEWED {date}", color: "#2f9e44" },
  { id: "signed-by", text: "SIGNED · {name}", color: "#1971c2" },
];

/** Substitute {date} / {name} tokens ({name} needs the author setting —
 *  presets using it are hidden until one is set). */
export function stampText(preset: StampPreset, name: string): string {
  return preset.text
    .replace("{date}", new Date().toLocaleDateString())
    .replace("{name}", name);
}

/** Render bordered rubber-stamp text to a PNG data URL (3× for crisp
 *  downscaling; the placement flow sizes stamps in points itself). */
export function renderTextStamp(text: string, color: string): PendingStamp {
  const k = 3;
  const font = `bold ${28 * k}px Helvetica, Arial, sans-serif`;
  const probe = document.createElement("canvas").getContext("2d")!;
  probe.font = font;
  const metrics = probe.measureText(text);
  const padX = 18 * k;
  const padY = 12 * k;
  const border = 3 * k;
  const w = Math.ceil(metrics.width) + padX * 2 + border * 2;
  const h = 28 * k + padY * 2 + border * 2;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.strokeStyle = color;
  ctx.lineWidth = border;
  const r = 6 * k;
  ctx.beginPath();
  ctx.roundRect(border / 2, border / 2, w - border, h - border, r);
  ctx.stroke();
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, w / 2, h / 2 + 1 * k);
  return { dataUrl: canvas.toDataURL("image/png"), width: w / k, height: h / k };
}

// ───────────────────── custom stamps (IndexedDB) ─────────────────────

export interface CustomStamp {
  id: string;
  label: string;
  dataUrl: string;
  width: number;
  height: number;
}

const DB_NAME = "xtract-pdf-editor-stamps";
const STORE = "stamps";

function openStampDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("stamp store unavailable"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openStampDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = run(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("stamp store failed"));
    });
  } finally {
    db.close();
  }
}

export function listCustomStamps(): Promise<CustomStamp[]> {
  return withStore("readonly", (s) => s.getAll() as IDBRequest<CustomStamp[]>);
}

export async function saveCustomStamp(stamp: CustomStamp): Promise<void> {
  await withStore("readwrite", (s) => s.put(stamp));
}

export async function deleteCustomStamp(id: string): Promise<void> {
  await withStore("readwrite", (s) => s.delete(id));
}
